import { WizardAPI } from '@philips-internal/oswald-ui-components';
import { StepStage } from '@philips-internal/oswald-ui-constants';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import BaseController from '../BaseController';
import { AppointmentsSteps } from './pseudonym-types';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { EstablishmentListVisibility } from './pseudonym-interfaces';
import UserContext from '../../utils/user-context';
export default class PseudonymAppointmentsBaseController extends BaseController {

  protected setWizardAppointmentsContext(wizardAppointments: WizardAPI): void {
    this.context.set({ wizardAppointments: wizardAppointments });
  }

  protected getWizardAppointmentsContext(): WizardAPI {
    return this.context.get('wizardAppointments') as WizardAPI;
  }

  protected setIsRescheduleContext(isReschedule: boolean): void {
    this.context.set({ isReschedule: isReschedule });
  }

  protected getIsRescheduleContext(): boolean {
    return this.context.get('isReschedule') as boolean;
  }

  protected setWizardStepDone(step: AppointmentsSteps, isDone: boolean): void {
    const wizardApi = this.getWizardAppointmentsContext();
    wizardApi.setStepDone(step, isDone);
  }

  protected async getEstablishmentListVisibility(): Promise<boolean> {
    const key = UserContext.holderPatientId;
    if (!PseudonymAppointmentModel.establishmentVisibilityControl.has(key)) {
      const response = await HttpService.get(APPLICATION_ENDPOINTS.GET_ESTABLISHMENT_LIST_VISIBILITY);
      const body = response.body as EstablishmentListVisibility;
      const isVisible = body.isVisible === 'S';
      PseudonymAppointmentModel.establishmentVisibilityControl.set(key, isVisible);
      return isVisible;
    }
    return PseudonymAppointmentModel.establishmentVisibilityControl.get(key);
  }

  protected setWizardShowStep(step: AppointmentsSteps): void {
    if (PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment > 0 && step === AppointmentsSteps.STEP_5_LOCATION) {
      return;
    }

    const wizardApi = this.getWizardAppointmentsContext();
    const hiddenSteps = [...wizardApi.getHiddenSteps()];

    if (hiddenSteps) {
      const index = hiddenSteps.indexOf(step);

      if (index >= 0) {
        hiddenSteps.splice(index, 1);
        wizardApi.setHiddenSteps(hiddenSteps);
      }
    }
  }

  protected setWizardHiddenStep(step: AppointmentsSteps): void {
    if (!this.isWizardStepHidden(step)) {
      const wizardApi = this.getWizardAppointmentsContext();
      const hiddenSteps = [...wizardApi.getHiddenSteps(), step];
      wizardApi.setHiddenSteps(hiddenSteps);
    }
  }

  protected isWizardStepShowing(step: AppointmentsSteps): boolean {
    return !this.isWizardStepHidden(step);
  }

  protected isWizardStepHidden(step: AppointmentsSteps): boolean {
    const wizardApi = this.getWizardAppointmentsContext();
    return wizardApi.getHiddenSteps().includes(step);
  }

  protected setNextStep(): void {
    const wizardApi = this.getWizardAppointmentsContext();
    wizardApi.nextStep();
  }

  protected setPrevStep(): void {
    const wizardApi = this.getWizardAppointmentsContext();
    wizardApi.prevStep();
  }

  protected skipWizardHiddenStep(from: string, to: string | undefined): void {
    const stepTo = to as AppointmentsSteps;
    if (from && to && this.isWizardStepHidden(stepTo)) {
      const wizardApi = this.getWizardAppointmentsContext();
      const steps = wizardApi.getSteps();
      const stepFrom = steps[from];

      wizardApi.setStepDone(to, true);
      if (stepFrom.stage === StepStage.PREV) {
        wizardApi.nextStep();
      } else if (stepFrom.stage === StepStage.NEXT) {
        wizardApi.prevStep();
      }
    }
  }

  protected showToastNotificationLoadDataError(): void {
    showToastNotificationError(MessageService.translate('tws.toast-notification.load-data-error'));
  }

  protected showLocationStep(healthInsuranceId: number): void {
    HttpService.get(APPLICATION_ENDPOINTS.NEW_APPOINTMENT_ESTABLISHMENT_LOCALE, {cdConvenio: healthInsuranceId}).then(response => {
      if (response?.body) {
        const locationList = response.body as [];
        if (!this.getIsRescheduleContext()) {
          this.controlLocationStepShow(locationList.length);
        }
      }
    })
  }

  private controlLocationStepShow(quantityRecords: number): void {
    const hiddenSteps = this.getWizardAppointmentsContext().getHiddenSteps();
    const isLocationHidden = hiddenSteps.includes(AppointmentsSteps.STEP_5_LOCATION);
    if (quantityRecords <= 0 && !isLocationHidden) {
      this.setWizardHiddenStep(AppointmentsSteps.STEP_5_LOCATION);
    } else if (quantityRecords > 0 && isLocationHidden) {
      this.setWizardShowStep(AppointmentsSteps.STEP_5_LOCATION);
    }
  }

  protected handleCheckupStep(): void {
    const stepsCheckup = [
      AppointmentsSteps.STEP_CHECKUP_SCHEDULE,
      AppointmentsSteps.STEP_CHECKUP_SUGGESTION,
    ];

    const stepsCommons = [
      AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION,
      AppointmentsSteps.STEP_4_QUIZ,
      AppointmentsSteps.STEP_5_LOCATION,
      AppointmentsSteps.STEP_6_SCHEDULE_PERIOD,
      AppointmentsSteps.STEP_7_SUGGESTION,
      AppointmentsSteps.STEP_DOCUMENTS,
    ];

    if (!this.isWizardStepHidden(AppointmentsSteps.STEP_5_LOCATION)) {
      stepsCommons.push(AppointmentsSteps.STEP_5_LOCATION);
    }

    const stepToShow = PseudonymAppointmentModel.stepTwo.hasCheckup ? stepsCheckup : stepsCommons;
    const stepToHide = PseudonymAppointmentModel.stepTwo.hasCheckup ? stepsCommons : stepsCheckup;

    stepToShow.forEach(step => this.setWizardShowStep(step));
    stepToHide.forEach(step => this.setWizardHiddenStep(step));
  }

  protected hideInitialSteps(validator: boolean): void {
    if (validator) {
      this.setWizardHiddenStep(AppointmentsSteps.STEP_DOCUMENTS);
      this.setWizardHiddenStep(AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION);
      this.setWizardHiddenStep(AppointmentsSteps.STEP_4_QUIZ);
      this.setWizardHiddenStep(AppointmentsSteps.STEP_5_LOCATION);
      this.setWizardHiddenStep(AppointmentsSteps.STEP_6_SCHEDULE_PERIOD);
      this.setWizardHiddenStep(AppointmentsSteps.STEP_7_SUGGESTION);
    }

    if (this.historyService.getCurrentRoute().includes(APPLICATION_ROUTES.RESCHEDULE_CHECKUP)) {
      this.setWizardHiddenStep(AppointmentsSteps.STEP_1_CHOOSE_PATIENT);
      this.setWizardHiddenStep(AppointmentsSteps.STEP_2_SCHEDULING_ITEMS);
    }
  }
}
